import { api } from './api';

/**
 * Normalize paginated or array API payloads after ApiResponse unwrap (results vs raw array).
 * @param {*} data
 * @returns {Array}
 */
export function extractSocietyResults(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  if (data.data != null && Array.isArray(data.data.results)) return data.data.results;
  return [];
}

const societyService = {
  // Get all societies with filters and pagination
  async getSocieties(params = {}) {
    try {
      const response = await api.get('/societies/', { params });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to fetch societies',
      );
    }
  },

  // Get single society by ID
  async getSociety(id) {
    try {
      const response = await api.get(`/societies/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch society');
    }
  },

  // Get cities list (optionally include cities that only have non-public societies — signup)
  async getCities(options = {}) {
    try {
      const params = {};
      if (options.forRegistration) {
        params.for_registration = 'true';
      }
      const response = await api.get('/societies/cities/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch cities');
    }
  },

  /**
   * Get cities with listing counts (businesses + properties + events) for Explore by City section.
   * @returns {Promise<{ cities: Array<{ city: string, slug: string, listing_count: number }> }>}
   */
  async getCityListingCounts() {
    try {
      const response = await api.get('/societies/cities-with-counts/');
      const data = response.data;
      if (!data || !Array.isArray(data.cities)) {
        return { cities: [] };
      }
      return { cities: data.cities };
    } catch (error) {
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to fetch city listings');
    }
  },

  // Create new society
  async createSociety(data) {
    try {
      const hasImages = Array.isArray(data?.images) && data.images.length > 0;
      let response;
      if (hasImages) {
        const formData = new FormData();
        Object.keys(data).forEach((key) => {
          if (key === 'images' && Array.isArray(data[key])) {
            data[key].forEach((file) => formData.append('images', file));
          } else if (key === 'is_public') {
            formData.append(key, data[key] ? 'true' : 'false');
          } else if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
            formData.append(key, data[key]);
          }
        });
        response = await api.post('/societies/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        });
      } else {
        response = await api.post('/societies/', data);
      }
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create society',
      );
    }
  },

  // Update society (JSON or multipart when appending images)
  async updateSociety(identifier, data) {
    try {
      const hasImages = Array.isArray(data?.images) && data.images.length > 0;
      let response;
      if (hasImages) {
        const formData = new FormData();
        Object.keys(data).forEach((key) => {
          if (key === 'images' && Array.isArray(data[key])) {
            data[key].forEach((file) => formData.append('images', file));
          } else if (key === 'is_public') {
            formData.append(key, data[key] ? 'true' : 'false');
          } else if (key === 'contact_numbers' && Array.isArray(data[key])) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
            formData.append(key, data[key]);
          }
        });
        response = await api.patch(`/societies/${identifier}/`, formData, {
          timeout: 120000,
        });
      } else {
        response = await api.patch(`/societies/${identifier}/`, data);
      }
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to update society',
      );
    }
  },

  // Delete society
  async deleteSociety(id) {
    try {
      const response = await api.delete(`/societies/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to delete society');
    }
  },

  // Search societies
  async searchSocieties(query, filters = {}) {
    try {
      const params = {
        search: query,
        ...filters
      };
      const response = await api.get('/societies/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to search societies');
    }
  },

  // Get societies by city
  async getSocietiesByCity(city, filters = {}) {
    try {
      const params = {
        city: city,
        ...filters
      };
      const response = await api.get('/societies/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch societies by city');
    }
  },

  /**
   * Fetch all societies for a city across all pages (for registration / signup picker).
   * Optional search: for registration, backend matches society name only (not description).
   * Without forRegistration, search includes name, description, city.
   * forRegistration: include non-public societies in that city (signup only).
   */
  async getSocietiesForCityAll(city, search = '', options = {}) {
    const all = [];
    let page = 1;
    const pageSize = 500;
    const { forRegistration = false } = options;
    try {
      while (true) {
        const params = {
          city,
          page_size: pageSize,
          page,
        };
        const q = (search || '').trim();
        if (q) params.search = q;
        if (forRegistration) params.for_registration = 'true';
        const data = await this.getSocieties(params);
        const results = extractSocietyResults(data);
        all.push(...results);
        if (results.length < pageSize) break;
        page += 1;
      }
      return all;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Signup: user requests a missing society/area (AllowAny + throttle on backend).
   */
  async submitSocietyAreaSuggestion(payload) {
    try {
      const response = await api.post('/societies/area-suggestions/', {
        requested_name: (payload.requested_name || '').trim(),
        city: (payload.city || '').trim(),
        requester_email: (payload.requester_email || '').trim(),
        details: (payload.details || '').trim(),
      });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to submit suggestion',
      );
    }
  },

  // Get top rated societies
  async getTopRatedSocieties(limit = 10) {
    try {
      const params = {
        ordering: '-rating',
        page_size: limit
      };
      const response = await api.get('/societies/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch top rated societies');
    }
  },

  // Get recently added societies
  async getRecentSocieties(limit = 10) {
    try {
      const params = {
        ordering: '-created_at',
        page_size: limit
      };
      const response = await api.get('/societies/', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch recent societies');
    }
  },

  // Follow a society
  async followSociety(societyId) {
    try {
      const response = await api.post(`/societies/${societyId}/follow/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to follow society');
    }
  },

  // Unfollow a society
  async unfollowSociety(societyId) {
    try {
      const response = await api.delete(`/societies/${societyId}/follow/`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to unfollow society');
    }
  },

  // Toggle follow status (follow if not following, unfollow if following)
  async toggleFollowSociety(societyId, isCurrentlyFollowing) {
    try {
      if (isCurrentlyFollowing) {
        return await this.unfollowSociety(societyId);
      } else {
        return await this.followSociety(societyId);
      }
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to toggle follow status');
    }
  },

  // Get societies followed by current user
  async getFollowedSocieties() {
    try {
      const response = await api.get('/societies/followed/');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch followed societies');
    }
  }
};

export { societyService };
